/**
 * lyrics-canvas — ONE canvas painter for synced lyrics, shared by every tool
 * that burns lyrics into a frame.
 *
 * Consumers:
 *   • /audio-to-video  — overlay inside its renderFrame (preview AND all three
 *                        export paths call the same renderFrame, so a lyric
 *                        drawn here is identical in preview and in the file).
 *   • /karaoke         — karaoke-video-export.js delegates its lyric block here
 *                        and keeps only its own background / progress bar.
 *
 * WHY A SHARED MODULE
 * The lyric block was written once inside karaoke-video-export.js. Copying it
 * into audio-to-video would have created a second painter to keep in sync, and
 * the two would have drifted on exactly the details that are easy to get wrong
 * (RTL word order, word-wipe boundaries, what happens between two lines). One
 * painter also means one place to fix a rendering bug for every tool.
 *
 * WHAT THIS ADDS OVER THE KARAOKE ORIGINAL
 *   • Line WRAPPING. The original drew each lyric as a single unwrapped
 *     fillText, which silently ran off the edge of a 9:16 frame — the most
 *     common aspect ratio for a lyric video. Rows are measured and wrapped to
 *     maxWidth, and the word-wipe follows the wrap.
 *   • 'smooth' wipe (a clipped sweep through the current word) next to the
 *     original discrete per-word 'word' wipe.
 *   • maxHoldS, so a line does not hang on screen through a 30 s instrumental
 *     break. A parsed LRC gives every line endS = the NEXT line's start, so
 *     without this the last line before a break stays lit until the break ends.
 *
 * A "timeline" is the KaraokeLyrics shape — the single source of truth:
 *   { startS, endS, text, words?:[{startS, text}] }
 *
 *   LyricsCanvas.draw(ctx, W, H, timeline, timeS, opts)
 *
 * Pure function of (timeline, timeS, opts): no state, no DOM, no time source of
 * its own. That is what lets the preview and the frame-by-frame export produce
 * the same pixels. Exposed as window.LyricsCanvas and module.exports (node tests).
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.LyricsCanvas = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var DEFAULTS = {
        mode: 'stack',          // 'stack' = active + neighbours · 'single' = active only
        neighbors: 2,           // lines above/below in 'stack' mode
        align: 'center',        // horizontal alignment of each row
        rtl: false,
        font: 'Inter, "Segoe UI", system-ui, Arial, sans-serif',
        activeWeight: '800',
        inactiveWeight: '600',
        activeColor: '#38bdf8',
        pastColor: 'rgba(255,255,255,0.28)',
        upcomingColor: 'rgba(255,255,255,0.5)',
        unsungColor: 'rgba(255,255,255,0.35)',
        wordWipe: true,
        wipeStyle: 'word',      // 'word' (discrete) · 'smooth' (clipped sweep)
        shadow: false,
        shadowColor: 'rgba(0,0,0,0.6)',
        maxHoldS: 0,            // 0 = a line stays until the next one
        fadeS: 0,               // per-line fade in/out, seconds
        wrap: true,
    };

    function opt(opts, key) {
        return (opts && opts[key] !== undefined && opts[key] !== null) ? opts[key] : DEFAULTS[key];
    }

    /* ── Tokenise a line into drawable pieces ───────────────────────────────
       Enhanced-LRC words already carry their own trailing space, so keeping the
       raw text reproduces the line verbatim. Plain text is split the same way
       so both paths share one layout/wipe implementation. */
    /* ── Tokenise a line into drawable pieces ────────────────────────
       Tokens are joined with join('') when a row is painted, so whatever spacing
       the line needs has to live INSIDE the tokens.

       This used to be left to the producer, documented as "Enhanced-LRC words
       already carry their own trailing space". That is true of the LRC parser,
       which slices the raw text between word tags — and false of every producer
       that builds words from a tokenizer. lyrics-ai-timing.js emits bare words
       (`{startS, text}` straight off the aligner), so every AI-timed line was
       painted glued: report #1530 (максим, /audio-to-video, 30.08) — «титры идут
       без пробелов между слов, буквы появляются быстрее песни». Both halves are
       this: with no word boundaries a smooth wipe reads as letters crawling
       rather than words landing. Measured on prod: 64 of 65 AI alignments in two
       months were word-level, i.e. every one of them.

       So spacing is derived here instead of trusted. `line.text` is the
       authority — it is what the producer wrote for humans — and the tokens are
       re-cut from it, keeping each word's timestamp.

       ⚠️ Deliberately NOT "append a space to every token but the last": that
       would wreck Chinese and Japanese, where the line legitimately has no
       spaces at all. Re-cutting from line.text gives CJK no spaces and Cyrillic
       its spaces without either being a special case.

       ⚠️ If the words do not correspond to line.text (a producer that rewrites,
       or a line where text was edited after timing), nothing is re-cut and the
       tokens are used as given. A wrong reconciliation would move words between
       timestamps, which is worse than the spacing it fixes. */
    function spacedWords(words, lineText) {
        var text = String(lineText == null ? '' : lineText);
        if (!text) return null;
        var out = [], pos = 0;
        for (var i = 0; i < words.length; i++) {
            var raw = String(words[i].text == null ? '' : words[i].text);
            var bare = raw.replace(/\s+/g, '');
            if (!bare) return null;                 // a token we cannot locate
            var at = text.indexOf(bare, pos);
            if (at < 0) return null;                // words are not this line
            out.push({ from: at, to: at + bare.length, startS: words[i].startS });
            pos = at + bare.length;
        }
        if (!out.length) return null;
        /* Each token carries the text up to where the NEXT one begins, so the
           separators between words (and only those) ride along. Leading text
           before the first token joins the first token, trailing text the last:
           nothing in the line is dropped. */
        var toks = [];
        for (var k = 0; k < out.length; k++) {
            var start = (k === 0) ? 0 : out[k].from;
            var end = (k === out.length - 1) ? text.length : out[k + 1].from;
            toks.push({ text: text.slice(start, end), startS: out[k].startS });
        }
        return toks;
    }

    function tokensOf(line) {
        if (line.words && line.words.length) {
            var recut = spacedWords(line.words, line.text);
            if (recut) return recut;
            return line.words.map(function (w) {
                return { text: String(w.text == null ? '' : w.text), startS: w.startS };
            });
        }
        var parts = String(line.text == null ? '' : line.text).split(/ +/).filter(function (p) { return p !== ''; });
        return parts.map(function (p, i) {
            return { text: i < parts.length - 1 ? p + ' ' : p, startS: null };
        });
    }

    /* Greedy wrap into rows. ctx.font must already be set — widths depend on it.
       A single token wider than maxWidth gets its own row rather than looping. */
    function layout(ctx, tokens, maxWidth, wrap) {
        var rows = [], cur = [], curW = 0;
        for (var i = 0; i < tokens.length; i++) {
            var t = tokens[i];
            var w = ctx.measureText(t.text).width;
            if (wrap && cur.length && curW + w > maxWidth) {
                rows.push({ tokens: cur, width: curW });
                cur = []; curW = 0;
            }
            cur.push({ text: t.text, startS: t.startS, w: w });
            curW += w;
        }
        if (cur.length) rows.push({ tokens: cur, width: curW });
        // Joined text lets a row that needs no per-word colouring be drawn with a
        // single fillText — fewer calls, and the browser does the bidi layout
        // itself instead of us reversing tokens by hand (which is only necessary
        // when each word needs its own colour).
        for (var r = 0; r < rows.length; r++) {
            rows[r].text = rows[r].tokens.map(function (t) { return t.text; }).join('');
        }
        return rows;
    }

    function rowStartX(row, cx, align, W, pad) {
        if (align === 'left') return pad;
        if (align === 'right') return W - pad - row.width;
        return cx - row.width / 2;
    }

    /* One row, one colour, one fillText — bidi handled by the engine. */
    function drawRowSolid(ctx, row, x, y, color) {
        ctx.fillStyle = color;
        ctx.fillText(row.text, x, y);
    }

    /* Draw one row token-by-token, for the word-wipe only. In RTL the VISUAL
       order is reversed while each token keeps its own measured width, so the
       line reads right to left without re-measuring. */
    function drawRow(ctx, row, x, y, rtl, colorFor) {
        var n = row.tokens.length;
        for (var j = 0; j < n; j++) {
            var idx = rtl ? (n - 1 - j) : j;
            var tk = row.tokens[idx];
            ctx.fillStyle = colorFor(tk);
            ctx.fillText(tk.text, x, y);
            x += tk.w;
        }
    }

    /* Absolute index of a token within its line, so the wipe can compare
       against a flat word sequence regardless of where the wrap fell. */
    function flatten(rows) {
        var out = [];
        for (var r = 0; r < rows.length; r++) {
            for (var i = 0; i < rows[r].tokens.length; i++) out.push(rows[r].tokens[i]);
        }
        return out;
    }

    /* Smooth wipe: paint the row unsung, then clip to [rowStart, wipeX] and
       repaint it sung. wipeX interpolates THROUGH the current word, which is
       what makes the sweep continuous instead of stepping word to word. */
    function drawRowSmooth(ctx, row, x, y, rtl, sungColor, unsungColor, wipeX, rowH) {
        drawRowSolid(ctx, row, x, y, unsungColor);
        if (wipeX <= x) return;
        ctx.save();
        ctx.beginPath();
        if (rtl) {
            // Sweeping right→left: the sung part is the RIGHT slice of the row.
            var fromRight = x + row.width - (wipeX - x);
            ctx.rect(fromRight, y - rowH, x + row.width - fromRight, rowH * 2);
        } else {
            ctx.rect(x, y - rowH, wipeX - x, rowH * 2);
        }
        ctx.clip();
        drawRow(ctx, row, x, y, rtl, function () { return sungColor; });
        ctx.restore();
    }

    /* ── When is a line READABLE? ────────────────────────────────────────────
       A fade used to START at the line's timestamp: alpha 0 at startS, 1 at
       startS + fadeS. Measured on a real export (2026-09-23, fadeS 0.35 — the
       /audio-to-video default): first ink +125 ms, half-opaque +250 ms, fully
       legible +375 ms after the sound. Viewers notice LATE lyrics at +0.22 s
       and EARLY ones only at −0.33 s (Masclef et al., ISMIR 2021, karaoke
       setting) — so the default look put EVERY line past the "late" threshold,
       whatever produced its timestamp: an .lrc file, the aligner or a tap.
       Report of 20.09.2026 (tembrica.com/de): «Songtext ist nicht synchron».

       So the fade now ENDS at startS: a line is fully legible the moment it is
       sung, and its dissolve midpoint sits fadeS/2 early — on the tolerated
       side. In 'single' mode the outgoing line fades out over the same window
       (a crossfade in place) instead of leaving a blank gap around every change.
       The lead never takes more than half of the previous line, so a burst of
       short lines cannot pile three texts on top of each other. */
    function fadeInSpan(timeline, i, fadeS) {
        if (!fadeS) return 0;
        var s = timeline[i].startS;
        var room = (i > 0) ? (s - timeline[i - 1].startS) / 2 : s;   // nothing can lead before 0:00
        return Math.max(0, Math.min(fadeS, room));
    }

    function fadeOutSpan(line, hideAt, fadeS) {
        if (!fadeS || hideAt == null) return 0;
        return Math.max(0, Math.min(fadeS, (hideAt - line.startS) / 2));
    }

    /* Per-line alpha: in over [startS − inSpan, startS], out over
       [hideAt − outSpan, hideAt]. */
    function alphaFor(line, timeS, inSpan, hideAt, outSpan) {
        var a = 1;
        if (timeS < line.startS) a = (inSpan > 0) ? Math.max(0, 1 - (line.startS - timeS) / inSpan) : 0;
        if (hideAt != null) {
            if (timeS >= hideAt) return 0;
            if (outSpan > 0 && hideAt - timeS < outSpan) a = Math.min(a, (hideAt - timeS) / outSpan);
        }
        return a;
    }

    /* When does this line stop being shown?  endS normally, but maxHoldS caps
       how long it can linger through an instrumental gap. */
    function hideTimeOf(line, maxHoldS) {
        var end = (typeof line.endS === 'number' && line.endS > line.startS) ? line.endS : null;
        if (maxHoldS > 0) {
            var capped = line.startS + maxHoldS;
            end = (end == null) ? capped : Math.min(end, capped);
        }
        return end;
    }

    /**
     * Draw the lyric block. Returns the active line index (-1 when nothing is
     * sung yet) so a caller can drive UI off the same decision the pixels used.
     *
     * Sizing is in CANVAS pixels and comes from the caller — audio-to-video
     * scales by its own frame height so the preview and the export agree.
     */
    function draw(ctx, W, H, timeline, timeS, opts) {
        opts = opts || {};
        if (!timeline || !timeline.length) return -1;

        var mode = opt(opts, 'mode');
        var rtl = !!opt(opts, 'rtl');
        var align = opt(opts, 'align');
        var font = opt(opts, 'font');
        var wrap = !!opt(opts, 'wrap');
        var maxHoldS = opt(opts, 'maxHoldS') || 0;
        var fadeS = opt(opts, 'fadeS') || 0;
        var wordWipe = !!opt(opts, 'wordWipe');
        var wipeStyle = opt(opts, 'wipeStyle');

        var activeSize = opts.activeSize || Math.round(H * 0.072);
        var inactiveSize = opts.inactiveSize || Math.round(H * 0.05);
        var lineGap = opts.lineGap || Math.round(H * 0.11);
        var rowGap = opts.rowGap || Math.round(activeSize * 0.18);
        var pad = (opts.padding != null) ? opts.padding : Math.round(W * 0.06);
        var maxWidth = opts.maxWidth || (W - pad * 2);
        var cx = (opts.cx != null) ? opts.cx : W / 2;
        var cy = (opts.cy != null) ? opts.cy : H / 2;
        var neighbors = (mode === 'single') ? 0 : (opts.neighbors != null ? opts.neighbors : DEFAULTS.neighbors);

        var idx = KL_activeIndexAt(timeline, timeS);

        /* Which lines are on screen, and how opaque. 'single' paints at most two
           (the outgoing and the incoming line, crossfading in place); 'stack'
           paints one block around an anchor line. */
        var shown = [];           // single: [{ i, alpha }]
        var anchor = idx;         // stack: line the block is centred on
        var anchorAlpha = 1, blockAlpha = 1;
        var incoming = idx + 1;   // the line whose fade-in may already have begun
        var inStart = (incoming < timeline.length)
            ? timeline[incoming].startS - fadeInSpan(timeline, incoming, fadeS) : Infinity;

        if (mode === 'single') {
            if (idx >= 0) {
                var cur = timeline[idx], curHide = hideTimeOf(cur, maxHoldS);
                var aCur = alphaFor(cur, timeS, 0, curHide, fadeOutSpan(cur, curHide, fadeS));
                if (aCur > 0) shown.push({ i: idx, alpha: aCur });
            }
            if (timeS >= inStart) {
                shown.push({ i: incoming, alpha: alphaFor(timeline[incoming], timeS, fadeInSpan(timeline, incoming, fadeS), null, 0) });
            }
            if (!shown.length) return -1;
        } else {
            if (idx < 0) {
                // Nothing sung yet: the block may already be fading in for line 0.
                if (!(timeS >= inStart)) return -1;
                anchor = 0;
                blockAlpha = anchorAlpha = alphaFor(timeline[0], timeS, fadeInSpan(timeline, 0, fadeS), null, 0);
            } else {
                // Mid-song the new active line was ALREADY on screen as the
                // upcoming neighbour, so it is not faded in again — that read as a
                // blink: alpha 0 at the very moment it is sung. It still fades out
                // at its hold cap, and in 'single' mode that is a blank screen.
                var act = timeline[anchor], actHide = hideTimeOf(act, maxHoldS);
                anchorAlpha = alphaFor(act, timeS, 0, actHide, fadeOutSpan(act, actHide, fadeS));
            }
        }

        ctx.save();
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';       // rows are positioned manually
        ctx.direction = rtl ? 'rtl' : 'ltr';
        if (opt(opts, 'shadow')) {
            ctx.shadowColor = opt(opts, 'shadowColor');
            ctx.shadowBlur = Math.max(2, Math.round(activeSize * 0.18));
            ctx.shadowOffsetY = Math.max(1, Math.round(activeSize * 0.04));
        }

        // ── Lay every visible line out first, so the block can be centred on
        //    the ACTIVE line even when neighbours wrap to different heights.
        function measure(i, isActive, off, alpha) {
            var size = isActive ? activeSize : inactiveSize;
            ctx.font = (isActive ? opt(opts, 'activeWeight') : opt(opts, 'inactiveWeight')) + ' ' + size + 'px ' + font;
            var rows = layout(ctx, tokensOf(timeline[i]), maxWidth, wrap);
            var h = rows.length * size + (rows.length - 1) * rowGap;
            return { i: i, off: off, isActive: isActive, size: size, rows: rows, height: h, alpha: alpha };
        }

        var blocks = [];
        if (mode === 'single') {
            for (var sI = 0; sI < shown.length; sI++) {
                var one = measure(shown[sI].i, true, 0, shown[sI].alpha);
                one.top = cy - one.height / 2;
                blocks.push(one);
            }
        } else {
            for (var off = -neighbors; off <= neighbors; off++) {
                var i = anchor + off;
                if (i < 0 || i >= timeline.length) continue;
                blocks.push(measure(i, off === 0, off, off === 0 ? anchorAlpha : blockAlpha));
            }

            var activeBlock = null, byOff = {};
            for (var b = 0; b < blocks.length; b++) {
                byOff[blocks[b].off] = blocks[b];
                if (blocks[b].isActive) activeBlock = blocks[b];
            }

            // Vertical placement: the active block is centred on cy, neighbours
            // stack outward by their OWN measured heights (a wrapped neighbour is
            // taller than one row) plus the inter-line gap. Both directions must
            // walk outward from the active line — nearest neighbour first — so the
            // offsets accumulate in the right order.
            var activeH = activeBlock ? activeBlock.height : 0;
            var activeTop = cy - activeH / 2;
            if (activeBlock) activeBlock.top = activeTop;

            var gapExtra = Math.max(0, lineGap - activeSize);
            var cursorUp = activeTop;
            for (var u = 1; u <= neighbors; u++) {
                var blk = byOff[-u];
                if (!blk) continue;
                cursorUp -= gapExtra + blk.height;
                blk.top = cursorUp;
            }
            var cursorDown = activeTop + activeH;
            for (var d = 1; d <= neighbors; d++) {
                var blk2 = byOff[d];
                if (!blk2) continue;
                cursorDown += gapExtra;
                blk2.top = cursorDown;
                cursorDown += blk2.height;
            }
        }

        // ── Paint ───────────────────────────────────────────────────────────
        for (var p = 0; p < blocks.length; p++) {
            var blkP = blocks[p];
            var line = timeline[blkP.i];
            var hideAt = hideTimeOf(line, maxHoldS);
            ctx.font = (blkP.isActive ? opt(opts, 'activeWeight') : opt(opts, 'inactiveWeight')) + ' ' + blkP.size + 'px ' + font;

            if (blkP.alpha <= 0) continue;
            ctx.globalAlpha = blkP.alpha;

            var flat = flatten(blkP.rows);
            var wipeOn = blkP.isActive && wordWipe && line.words && line.words.length > 0;
            // Index of the word being sung right now, within the flat sequence.
            var sungIdx = -1;
            if (wipeOn) {
                for (var s = 0; s < flat.length; s++) {
                    if (flat[s].startS != null && flat[s].startS <= timeS) sungIdx = s; else break;
                }
            }

            var consumed = 0;   // flat tokens already drawn (to locate the wipe row)
            var y = blkP.top + blkP.size / 2;
            for (var r = 0; r < blkP.rows.length; r++) {
                var row = blkP.rows[r];
                var x = rowStartX(row, cx, align, W, pad);

                if (!blkP.isActive) {
                    drawRowSolid(ctx, row, x, y, blkP.off < 0 ? opt(opts, 'pastColor') : opt(opts, 'upcomingColor'));
                } else if (!wipeOn) {
                    drawRowSolid(ctx, row, x, y, opt(opts, 'activeColor'));
                } else if (wipeStyle === 'smooth') {
                    var rowFirst = consumed, rowLast = consumed + row.tokens.length - 1;
                    var sung = opt(opts, 'activeColor'), unsung = opt(opts, 'unsungColor');
                    if (sungIdx >= rowLast) {
                        drawRowSolid(ctx, row, x, y, sung);
                    } else if (sungIdx < rowFirst) {
                        drawRowSolid(ctx, row, x, y, unsung);
                    } else {
                        // The sweep is inside this row — interpolate through the word.
                        var wIn = sungIdx - rowFirst;
                        var before = 0;
                        for (var q = 0; q < wIn; q++) before += row.tokens[q].w;
                        var curTok = row.tokens[wIn];
                        var nextStart = null;
                        for (var nx = sungIdx + 1; nx < flat.length; nx++) {
                            if (flat[nx].startS != null) { nextStart = flat[nx].startS; break; }
                        }
                        if (nextStart == null) nextStart = (hideAt != null ? hideAt : curTok.startS + 0.6);
                        var span = Math.max(0.001, nextStart - curTok.startS);
                        var frac = Math.max(0, Math.min(1, (timeS - curTok.startS) / span));
                        drawRowSmooth(ctx, row, x, y, rtl, sung, unsung, x + before + curTok.w * frac, blkP.size);
                    }
                } else {
                    var sungC = opt(opts, 'activeColor'), unsungC = opt(opts, 'unsungColor');
                    drawRow(ctx, row, x, y, rtl, function (tk) {
                        return (tk.startS != null && tk.startS <= timeS) ? sungC : unsungC;
                    });
                }

                consumed += row.tokens.length;
                y += blkP.size + rowGap;
            }
        }

        ctx.restore();
        return idx;
    }

    /* Local copy of the binary search so the painter works in a bare Node test
       (and if karaoke-lyrics.js has not loaded yet). KaraokeLyrics stays the
       source of truth when it IS present — same algorithm, one behaviour. */
    function activeIndexAt(timeline, timeS) {
        var lo = 0, hi = timeline.length - 1, idx = -1;
        while (lo <= hi) {
            var mid = (lo + hi) >> 1;
            if (timeline[mid].startS <= timeS) { idx = mid; lo = mid + 1; }
            else { hi = mid - 1; }
        }
        return idx;
    }
    function KL_activeIndexAt(timeline, timeS) {
        var g = (typeof window !== 'undefined') ? window : null;
        if (g && g.KaraokeLyrics && g.KaraokeLyrics.activeIndexAt) return g.KaraokeLyrics.activeIndexAt(timeline, timeS);
        return activeIndexAt(timeline, timeS);
    }

    return {
        draw: draw,
        activeIndexAt: activeIndexAt,
        // exposed for tests / callers that want to pre-measure a block
        _layout: layout,
        _tokensOf: tokensOf,
        _hideTimeOf: hideTimeOf,
        DEFAULTS: DEFAULTS,
    };
});
